import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Crown } from 'lucide-react';

/**
 * StickyMobileCTA — premium fixed-bottom "Start Free Trial" CTA bar.
 * Appears on mobile only (<lg), after the user scrolls past ~50% of viewport height,
 * and only on marketing/discovery pages (not signup, dashboard, checkout).
 *
 * Behavior:
 *   • <lg only (md and below).
 *   • Slides up after 0.5 × innerHeight scrolled.
 *   • Respects `prefers-reduced-motion`.
 *   • Hidden on /start, /complete-subscription, /login, /register, /dashboard, /onboarding, /menu, /r/.
 */
const HIDE_ON = [
  '/start',
  '/complete-subscription',
  '/login',
  '/register',
  '/dashboard',
  '/onboarding',
  '/admin',
  '/order',
  '/checkout',
];

const StickyMobileCTA = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const threshold = window.innerHeight * 0.5;
      setVisible(window.scrollY > threshold);
    };
    onScroll(); // run once on mount in case of refresh mid-page
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide on private/funnel routes and on routes that already contain the CTA prominently
  const shouldHide =
    HIDE_ON.some((p) => pathname.startsWith(p)) ||
    pathname.startsWith('/menu/') ||
    pathname.startsWith('/r/');

  if (shouldHide) return null;

  return (
    <div
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none transition-all duration-300 ease-out ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      aria-hidden={!visible}
      data-testid="sticky-mobile-cta"
    >
      {/* Soft fade-up so the CTA doesn't slam onto content */}
      <div
        className="h-6 bg-gradient-to-b from-transparent to-black/90 pointer-events-none"
        aria-hidden="true"
      />
      <div className="pointer-events-auto bg-black/95 backdrop-blur-xl border-t border-gold-500/25 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <a
          href="/start?plan=ordering&utm_source=sticky_mobile"
          data-testid="sticky-mobile-cta-btn"
          className="group relative w-full h-[54px] inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] tracking-[0.01em] transition-all shadow-[0_14px_32px_-10px_rgba(212,175,55,0.5),inset_0_1px_0_rgba(255,255,255,0.25)] active:translate-y-0.5 overflow-hidden"
        >
          <Crown className="w-4 h-4" />
          <span>Start Free Trial</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </a>
        <p className="mt-1.5 text-center text-[10.5px] text-white/45 tracking-wide">
          45-day free trial · Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default StickyMobileCTA;
